import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  ShieldCheck, RotateCcw, Save, Lock, AlertCircle, Info, Crown,
} from "lucide-react";
import { toast } from "sonner";
import { invalidatePermissionsCache } from "@/hooks/usePermissions";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PermissionGroupsPanel from "@/pages/PermissionGroupsPanel";

export default function PermissionsMatrixPage() {
  const { user } = useAuth();
  const isMaster = user?.role === "master";

  const [catalog, setCatalog] = useState(null); // modules, actions, roles
  const [matrix, setMatrix] = useState(null);
  const [flagEnabled, setFlagEnabled] = useState(false);
  const [selectedRole, setSelectedRole] = useState("rh_admin");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmToggle, setConfirmToggle] = useState(null); // null | true | false
  const [confirmReset, setConfirmReset] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        api.get("/permissions/catalog"),
        api.get("/permissions/matrix"),
      ]);
      setCatalog(c.data);
      setMatrix(m.data.matrix);
      setFlagEnabled(m.data.enabled);
      setDirty(false);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Falha ao carregar permissões");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const modulesFiltered = useMemo(() => {
    if (!catalog) return [];
    const s = search.trim().toLowerCase();
    return catalog.modules.filter((m) =>
      !s || m.label.toLowerCase().includes(s) || m.key.toLowerCase().includes(s) || (m.group || "").toLowerCase().includes(s)
    );
  }, [catalog, search]);

  const grouped = useMemo(() => {
    const g = {};
    modulesFiltered.forEach((m) => {
      (g[m.group] = g[m.group] || []).push(m);
    });
    return g;
  }, [modulesFiltered]);

  const toggleCell = (moduleKey, actionKey) => {
    if (!isMaster || selectedRole === "master") return; // Master imune
    setMatrix((prev) => {
      const next = { ...prev };
      next[selectedRole] = { ...(next[selectedRole] || {}) };
      next[selectedRole][moduleKey] = { ...(next[selectedRole][moduleKey] || {}) };
      next[selectedRole][moduleKey][actionKey] = !next[selectedRole][moduleKey][actionKey];
      return next;
    });
    setDirty(true);
  };

  const bulkRow = (moduleKey, value) => {
    if (!isMaster || selectedRole === "master") return;
    setMatrix((prev) => {
      const next = { ...prev };
      next[selectedRole] = { ...(next[selectedRole] || {}) };
      next[selectedRole][moduleKey] = {};
      catalog.actions.forEach((a) => { next[selectedRole][moduleKey][a.key] = value; });
      return next;
    });
    setDirty(true);
  };

  const bulkAll = (value) => {
    if (!isMaster || selectedRole === "master") return;
    setMatrix((prev) => {
      const next = { ...prev };
      next[selectedRole] = {};
      catalog.modules.forEach((m) => {
        next[selectedRole][m.key] = {};
        catalog.actions.forEach((a) => { next[selectedRole][m.key][a.key] = value; });
      });
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/permissions/matrix", { matrix });
      setMatrix(data.matrix);
      setDirty(false);
      invalidatePermissionsCache();
      toast.success("Matriz de permissões atualizada");
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  const doToggleFlag = async () => {
    const targetValue = !flagEnabled;
    setConfirmToggle(null);
    try {
      const { data } = await api.put("/permissions/settings/granular", { enabled: targetValue });
      setFlagEnabled(data.enabled);
      invalidatePermissionsCache();
      toast.success(`Sistema granular ${data.enabled ? "ATIVADO" : "DESATIVADO"}`);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Falha ao alterar flag");
    }
  };

  const doReset = async () => {
    setConfirmReset(false);
    try {
      const { data } = await api.post("/permissions/reset-defaults");
      setMatrix(data.matrix);
      setDirty(false);
      invalidatePermissionsCache();
      toast.success("Matriz restaurada para defaults");
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Falha ao restaurar");
    }
  };

  if (loading || !catalog || !matrix) {
    return <div className="p-8 text-muted-foreground" data-testid="permissions-loading">Carregando...</div>;
  }

  const roleLocked = selectedRole === "master";

  return (
    <div className="space-y-6" data-testid="permissions-matrix-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Administração · Segurança</div>
          <h1 className="font-display text-3xl font-semibold flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-[#234935]" /> Permissões Granulares
          </h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground max-w-3xl">
            Matriz <b>Módulo × Ação</b> por perfil. Master é sempre imune (proteção absoluta).
            Ative o modo granular via feature flag abaixo.
          </p>
        </div>
      </div>

      {!isMaster && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900 flex items-start gap-2" data-testid="permissions-readonly-notice">
          <Lock className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Você está em <b>modo leitura</b>. Somente o perfil Master pode alterar a matriz.</span>
        </div>
      )}

      <Tabs defaultValue="matrix" className="w-full">
        <TabsList data-testid="permissions-tabs">
          <TabsTrigger value="matrix" data-testid="tab-matrix">Matriz por Perfil</TabsTrigger>
          <TabsTrigger value="groups" data-testid="tab-groups">Grupos Customizados</TabsTrigger>
        </TabsList>

        <TabsContent value="matrix" className="space-y-6 mt-4">

      {/* Feature flag */}
      <div className="rounded-xl border border-border bg-white p-4 sm:p-5 flex items-center gap-4" data-testid="permissions-flag-card">
        <div className={`h-11 w-11 rounded-lg grid place-items-center ${flagEnabled ? "bg-emerald-100" : "bg-slate-100"}`}>
          <ShieldCheck className={`h-5 w-5 ${flagEnabled ? "text-emerald-700" : "text-slate-500"}`} />
        </div>
        <div className="flex-1">
          <div className="font-display text-[15px] font-semibold">Modo Granular · Feature Flag</div>
          <div className="text-[12.5px] text-muted-foreground">
            Enquanto <b>desligada</b>, as permissões clássicas por role continuam valendo (nenhuma regressão).
            Ligue apenas após revisar a matriz.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className={flagEnabled ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"} data-testid="permissions-flag-badge">
            {flagEnabled ? "ATIVO" : "DESATIVADO"}
          </Badge>
          <Switch checked={flagEnabled} disabled={!isMaster}
            onCheckedChange={() => setConfirmToggle(!flagEnabled)}
            data-testid="permissions-flag-switch"
          />
        </div>
      </div>

      {/* Controles */}
      <div className="rounded-xl border border-border bg-white p-4 sm:p-5">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="min-w-[220px]">
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Perfil</label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger data-testid="permissions-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {catalog.roles.map((r) => (
                  <SelectItem key={r.key} value={r.key} data-testid={`permissions-role-${r.key}`}>
                    {r.key === "master" ? <span className="flex items-center gap-1"><Crown className="h-3.5 w-3.5" />{r.label} · Imune</span> : r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Buscar módulo</label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ex: colaborador, auditoria..." data-testid="permissions-search" />
          </div>
          {isMaster && !roleLocked && (
            <div className="flex items-end gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={() => bulkAll(true)} data-testid="permissions-bulk-all-on">Marcar tudo</Button>
              <Button variant="outline" size="sm" onClick={() => bulkAll(false)} data-testid="permissions-bulk-all-off">Desmarcar tudo</Button>
            </div>
          )}
        </div>
        {roleLocked && (
          <div className="mt-3 rounded-md border border-[#E8D9AE] bg-[#F9F4E7] px-3 py-2 text-[12px] text-[#5C4320] flex items-start gap-2">
            <Crown className="h-3.5 w-3.5 mt-0.5 text-[#8A6B2B]" />
            <span><b>Master</b> tem acesso total garantido. Esta linha é somente leitura por segurança — não é editável nem por outro Master.</span>
          </div>
        )}
        {isMaster && dirty && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 flex items-center justify-between gap-2">
            <span className="flex items-center gap-2"><AlertCircle className="h-3.5 w-3.5" /> Alterações não salvas nesta sessão.</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => load()} data-testid="permissions-discard-btn">Descartar</Button>
              <Button size="sm" className="bg-[#234935] hover:bg-[#2c5c43]" onClick={save} disabled={saving} data-testid="permissions-save-btn">
                <Save className="h-4 w-4 mr-1" /> {saving ? "Salvando..." : "Salvar matriz"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Matriz */}
      <div className="rounded-xl border border-border bg-white overflow-hidden" data-testid="permissions-matrix">
        {Object.entries(grouped).map(([group, mods]) => (
          <div key={group}>
            <div className="bg-[#F1E9CF]/50 px-4 py-2 text-[10.5px] uppercase tracking-[0.18em] text-[#5C4320] font-bold border-b border-[#E8D9AE]">
              {group}
            </div>
            <div className="hidden sm:grid grid-cols-[minmax(220px,1fr)_repeat(6,90px)_120px] items-center px-4 py-1.5 bg-slate-50 text-[10.5px] uppercase tracking-wider text-muted-foreground font-semibold border-b border-slate-100">
              <span>Módulo</span>
              {catalog.actions.map((a) => (
                <span key={a.key} className="text-center">{a.label}</span>
              ))}
              <span className="text-right">Linha</span>
            </div>
            {mods.map((mod) => {
              const row = matrix[selectedRole]?.[mod.key] || {};
              const allOn = catalog.actions.every((a) => row[a.key]);
              const allOff = catalog.actions.every((a) => !row[a.key]);
              return (
                <div
                  key={mod.key}
                  className="grid grid-cols-[minmax(220px,1fr)_repeat(6,90px)_120px] items-center gap-2 px-4 py-2 border-b border-slate-100 text-[13px] hover:bg-slate-50/40"
                  data-testid={`permissions-row-${mod.key}`}
                >
                  <div>
                    <div className="font-medium">{mod.label}</div>
                    <div className="text-[10.5px] font-mono text-muted-foreground">{mod.key}</div>
                  </div>
                  {catalog.actions.map((a) => {
                    const on = !!row[a.key];
                    const disabled = !isMaster || roleLocked;
                    return (
                      <div key={a.key} className="text-center">
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={() => toggleCell(mod.key, a.key)}
                          data-testid={`permissions-cell-${mod.key}-${a.key}`}
                          className={`h-6 w-11 rounded-full border transition-colors ${on
                            ? "bg-emerald-500 border-emerald-600"
                            : "bg-slate-200 border-slate-300"} ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer hover:opacity-90"}`}
                          aria-label={`${a.label} ${on ? "ativo" : "inativo"}`}
                        >
                          <span className={`block h-4 w-4 bg-white rounded-full shadow transform transition-transform ${on ? "translate-x-6" : "translate-x-1"}`} />
                        </button>
                      </div>
                    );
                  })}
                  <div className="text-right flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" className="h-6 text-[10.5px] px-2"
                      disabled={!isMaster || roleLocked || allOn}
                      onClick={() => bulkRow(mod.key, true)}
                      data-testid={`permissions-row-all-${mod.key}`}
                    >Todos</Button>
                    <Button variant="ghost" size="sm" className="h-6 text-[10.5px] px-2"
                      disabled={!isMaster || roleLocked || allOff}
                      onClick={() => bulkRow(mod.key, false)}
                      data-testid={`permissions-row-none-${mod.key}`}
                    >Nenhum</Button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Ações finais */}
      {isMaster && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Button variant="outline" onClick={() => setConfirmReset(true)} data-testid="permissions-reset-btn">
            <RotateCcw className="h-4 w-4 mr-1" /> Restaurar defaults
          </Button>
          <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
            <Info className="h-3.5 w-3.5" />
            Cada alteração é registrada em auditoria (before/after completos).
          </div>
          <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={save} disabled={!dirty || saving} data-testid="permissions-save-footer-btn">
            <Save className="h-4 w-4 mr-1" /> {saving ? "Salvando..." : "Salvar matriz"}
          </Button>
        </div>
      )}
        </TabsContent>

        <TabsContent value="groups" className="mt-4">
          <PermissionGroupsPanel catalog={catalog} />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <Dialog open={confirmToggle !== null} onOpenChange={() => setConfirmToggle(null)}>
        <DialogContent data-testid="permissions-flag-confirm">
          <DialogHeader>
            <DialogTitle>{confirmToggle ? "Ativar modo granular?" : "Desativar modo granular?"}</DialogTitle>
            <DialogDescription>
              {confirmToggle
                ? "As permissões da matriz passarão a valer imediatamente para TODOS os usuários (exceto Master, que é imune). Certifique-se de que a matriz está revisada."
                : "As permissões voltarão ao comportamento clássico por role. Nenhuma matriz será apagada — pode religar depois."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmToggle(null)}>Cancelar</Button>
            <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={doToggleFlag} data-testid="permissions-flag-confirm-btn">
              {confirmToggle ? "Sim, ativar" : "Sim, desativar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmReset} onOpenChange={setConfirmReset}>
        <DialogContent data-testid="permissions-reset-confirm">
          <DialogHeader>
            <DialogTitle>Restaurar matriz para defaults?</DialogTitle>
            <DialogDescription>
              Substitui a matriz atual pelos defaults do sistema (baseado no comportamento clássico por role). Master mantém acesso total. Ação registrada em auditoria.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmReset(false)}>Cancelar</Button>
            <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={doReset} data-testid="permissions-reset-confirm-btn">Sim, restaurar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
